import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Bar as ChartBar, Doughnut as ChartDoughnut } from 'react-chartjs-2';
import { analyticsAPI, accountsAPI, groupsAPI, membersAPI } from '../services/api';
import Loader from '../components/Loader';
import { jsPDF } from 'jspdf';
import './Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  ChartTooltip,
  ChartLegend
);

// AnalyticsData interface removed as it's not used

const COLORS = ['#0e4842', '#14a085', '#32ff7e', '#1a8a3e', '#0a3d38', '#28e66d', '#10b981'];

// Memoized Chart Components for better performance
const MemoizedBarChart = memo(({ data, options }: { data: any; options: any }) => (
  <ChartBar data={data} options={options} />
));
MemoizedBarChart.displayName = 'MemoizedBarChart';

const MemoizedDoughnutChart = memo(({ data, options }: { data: any; options: any }) => (
  <ChartDoughnut data={data} options={options} />
));
MemoizedDoughnutChart.displayName = 'MemoizedDoughnutChart';

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination and filter states for member overlap
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [minGroupsFilter, setMinGroupsFilter] = useState<number | ''>('');
  const [sortBy, setSortBy] = useState<'name' | 'groups' | 'phone'>('groups');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Reports tab states
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [memberSearch, setMemberSearch] = useState('');
  const debouncedMemberSearch = useDebounce(memberSearch, 300);
  const [groupSearch, setGroupSearch] = useState('');
  const debouncedGroupSearch = useDebounce(groupSearch, 300);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage, setReportItemsPerPage] = useState(10);

  // Memoized query keys with proper dependencies
  const analyticsQueryKey = ['analytics', 'dashboard'];
  const accountComparisonQueryKey = ['analytics', 'account-comparison'];
  const memberOverlapQueryKey = ['analytics', 'member-overlap'];
  const accountsQueryKey = ['accounts'];
  const reportGroupsQueryKey = ['groups', 'account', selectedAccountId];
  const reportMembersQueryKey = ['members', 'account', selectedAccountId];

  // Memoized query functions
  const fetchAnalytics = useCallback(async () => {
    const response = await analyticsAPI.getDashboard();
    return response.data;
  }, []);

  const fetchAccountComparison = useCallback(async () => {
    const response = await analyticsAPI.getAccountComparison();
    return (response.data || []).map((account: any) => ({
      ...account,
      groupsCount: Number(account.groupsCount) || 0,
      membersCount: Number(account.membersCount) || 0,
      avgGroupSize: Number(account.avgGroupSize) || 0,
    }));
  }, []);

  const fetchMemberOverlap = useCallback(async () => {
    const response = await analyticsAPI.getMemberOverlap();
    return response.data;
  }, []);

  const fetchAccounts = useCallback(async () => {
    const response = await accountsAPI.getAll();
    return response.data;
  }, []);

  const fetchReportGroups = useCallback(async () => {
    if (!selectedAccountId) return [];
    const response = await groupsAPI.getByAccount(selectedAccountId);
    return Array.isArray(response.data) ? response.data : [];
  }, [selectedAccountId]);

  const fetchReportMembers = useCallback(async () => {
    if (!selectedAccountId) return { members: [] };
    const response = await membersAPI.getAll({ accountId: selectedAccountId, limit: 1000 });
    return response.data;
  }, [selectedAccountId]);

  // Fetch analytics with TanStack Query - optimized caching
  const { 
    data, 
    isLoading: loading,
    error: analyticsError 
  } = useQuery({
    queryKey: analyticsQueryKey,
    queryFn: fetchAnalytics,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchInterval: 120000, // Refetch every 2 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch account comparison - optimized for tab switching
  const { 
    data: accountComparison = [], 
    isLoading: comparisonLoading 
  } = useQuery({
    queryKey: accountComparisonQueryKey,
    queryFn: fetchAccountComparison,
    enabled: activeTab === 'comparison',
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch member overlap - optimized for tab switching
  const { 
    data: memberOverlap,
    isLoading: overlapLoading 
  } = useQuery({
    queryKey: memberOverlapQueryKey,
    queryFn: fetchMemberOverlap,
    enabled: activeTab === 'overlap',
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch accounts for reports - long cache time
  const { 
    data: accounts = [] 
  } = useQuery({
    queryKey: accountsQueryKey,
    queryFn: fetchAccounts,
    enabled: activeTab === 'reports',
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch report groups - optimized with account dependency
  const { 
    data: reportGroups = [], 
    isLoading: reportGroupsLoading 
  } = useQuery({
    queryKey: reportGroupsQueryKey,
    queryFn: fetchReportGroups,
    enabled: activeTab === 'reports' && !!selectedAccountId,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch report members - optimized with account dependency
  const { 
    data: reportMembersData, 
    isLoading: reportMembersLoading 
  } = useQuery({
    queryKey: reportMembersQueryKey,
    queryFn: fetchReportMembers,
    enabled: activeTab === 'reports' && !!selectedAccountId,
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const reportMembers = useMemo(() => 
    Array.isArray(reportMembersData?.members) ? reportMembersData.members : [],
    [reportMembersData]
  );

  // Deduplicate groups by _id (same group can appear multiple times per account)
  const uniqueReportGroups = useMemo(() => {
    if (!Array.isArray(reportGroups)) return [];
    const seen = new Set<string>();
    return reportGroups.filter((group: any) => {
      const id = group._id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [reportGroups]);

  // Deduplicate members by phoneNumber or id (same person can appear in multiple groups)
  const uniqueReportMembers = useMemo(() => {
    if (!Array.isArray(reportMembers)) return [];
    const seen = new Set<string>();
    return reportMembers.filter((member: any) => {
      const key = member.phoneNumber || member.id || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [reportMembers]);

  const reportLoading = reportGroupsLoading || reportMembersLoading;
  const error = analyticsError ? (analyticsError as any)?.response?.data?.error || 'Failed to fetch analytics' : null;

  // Set default account when accounts load
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId && activeTab === 'reports') {
      setSelectedAccountId(accounts[0]._id);
    }
  }, [accounts, selectedAccountId, activeTab]);

  // Memoized tab change handler
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setReportCurrentPage(1);
  }, []);

  const downloadPDF = async () => {
    if (!selectedAccountId) {
      alert('Please select an account first.');
      return;
    }

    try {
      const account = accounts.find((acc: any) => acc._id === selectedAccountId);
      const accountName = account?.accountName || 'Unknown Account';
      const accountPhone = account?.phoneNumber || 'N/A';
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      // Use unique members and groups for PDF report (not filtered by search, deduplicated)
      const allMembersForPDF = Array.isArray(uniqueReportMembers) ? uniqueReportMembers : [];
      const allGroupsForPDF = Array.isArray(uniqueReportGroups) ? uniqueReportGroups : [];
      
      console.log('PDF Generation - Members:', allMembersForPDF.length, 'Groups:', allGroupsForPDF.length);
      
      // Validate we have at least some data
      if (allMembersForPDF.length === 0 && allGroupsForPDF.length === 0) {
        alert('No data available to generate report. Please ensure the account is synced and data is loaded.');
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Professional table rendering function (replaces autoTable)
      const renderTable = (
        headers: string[],
        rows: string[][],
        columnWidths: number[],
        options: {
          startY?: number;
          fontSize?: number;
          headerColor?: [number, number, number];
          rowHeight?: number;
          margin?: { left: number; right: number };
        } = {}
      ): number => {
        const {
          startY = yPosition,
          fontSize = 9,
          headerColor = [14, 160, 133],
          rowHeight = 7,
          margin = { left: 20, right: 20 }
        } = options;

        let currentY = startY;
        const tableWidth = pageWidth - margin.left - margin.right;
        const totalColumnWidth = columnWidths.reduce((sum, w) => sum + w, 0);
        const scaleFactor = tableWidth / totalColumnWidth;
        const scaledWidths = columnWidths.map(w => w * scaleFactor);

        // Draw header
        pdf.setFillColor(headerColor[0], headerColor[1], headerColor[2]);
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.1);
        
        let xPos = margin.left;
        headers.forEach((header, colIndex) => {
          pdf.rect(xPos, currentY, scaledWidths[colIndex], rowHeight, 'FD');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(fontSize);
          pdf.setFont('helvetica', 'bold');
          const textX = xPos + 4; // Increased padding from 2 to 4
          const textY = currentY + rowHeight / 2 + fontSize / 3;
          pdf.text(header, textX, textY);
          xPos += scaledWidths[colIndex];
        });
        
        currentY += rowHeight;
        // Reset colors after header
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        // Ensure fill color is reset to white for rows
        pdf.setFillColor(255, 255, 255);

        // Draw rows
        rows.forEach((row, rowIndex) => {
          // Check if we need a new page
          if (currentY + rowHeight > pageHeight - 20) {
            pdf.addPage();
            currentY = 20;
            
            // Redraw header on new page
            pdf.setFillColor(headerColor[0], headerColor[1], headerColor[2]);
            pdf.setDrawColor(200, 200, 200);
            let headerXPos = margin.left;
            headers.forEach((header, colIndex) => {
              pdf.rect(headerXPos, currentY, scaledWidths[colIndex], rowHeight, 'FD');
              pdf.setTextColor(255, 255, 255);
              pdf.setFontSize(fontSize);
              pdf.setFont('helvetica', 'bold');
              const textX = headerXPos + 4; // Increased padding from 2 to 4
              const textY = currentY + rowHeight / 2 + fontSize / 3;
              pdf.text(header, textX, textY);
              headerXPos += scaledWidths[colIndex];
            });
            currentY += rowHeight;
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
            // Reset fill color after header redraw
            pdf.setFillColor(255, 255, 255);
          }

          // Alternate row color
          const rowFillColor = rowIndex % 2 === 0 ? [250, 250, 250] : [255, 255, 255];
          
          xPos = margin.left;
          row.forEach((cell, colIndex) => {
            // Set fill color before drawing rectangle
            pdf.setFillColor(rowFillColor[0], rowFillColor[1], rowFillColor[2]);
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.1);
            // Draw rectangle with fill and stroke
            pdf.rect(xPos, currentY, scaledWidths[colIndex], rowHeight, 'FD');
            
            // Set text color and draw text
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(fontSize);
            const textX = xPos + 4; // Increased padding from 2 to 4
            const textY = currentY + rowHeight / 2 + fontSize / 3;
            
            // Truncate text if too long (account for padding on both sides)
            const maxWidth = scaledWidths[colIndex] - 8; // 4mm padding on each side
            let displayText = cell;
            const textWidth = pdf.getTextWidth(displayText);
            if (textWidth > maxWidth) {
              while (pdf.getTextWidth(displayText + '...') > maxWidth && displayText.length > 0) {
                displayText = displayText.slice(0, -1);
              }
              displayText += '...';
            }
            
            pdf.text(displayText, textX, textY);
            xPos += scaledWidths[colIndex];
          });
          
          currentY += rowHeight;
        });

        return currentY;
      };

      // Title Page
      pdf.setFontSize(24);
      pdf.setTextColor(14, 160, 133);
      pdf.text('Account Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(accountName, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Phone: ${accountPhone}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      pdf.text(`Generated on: ${reportDate}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Executive Summary Section
      pdf.addPage();
      yPosition = 20;
      pdf.setFontSize(18);
      pdf.setTextColor(14, 160, 133);
      pdf.text('Executive Summary', 20, yPosition);
      yPosition += 20; // Increased spacing after section title

      // Helper function to sanitize string for PDF (defined early for use in summary)
      const sanitizeString = (str: any): string => {
        if (str === null || str === undefined) return '';
        // Convert to string and remove any problematic characters
        let cleaned = String(str);
        // Remove control characters by filtering
        cleaned = cleaned.split('').filter(char => {
          const code = char.charCodeAt(0);
          return code >= 32 || code === 9 || code === 10 || code === 13; // Keep printable chars, tab, newline, carriage return
        }).join('');
        return cleaned.trim() || '';
      };

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      const totalGroups = Array.isArray(allGroupsForPDF) ? allGroupsForPDF.length : 0;
      // Calculate total unique members (not counting duplicates across groups)
      const uniqueMembers = Array.isArray(allMembersForPDF) ? allMembersForPDF.length : 0;
      // Calculate average group size based on group participants (for group size metric)
      const totalParticipantsFromGroups = Array.isArray(allGroupsForPDF) 
        ? allGroupsForPDF.reduce((sum, group) => sum + (Number(group.participantsCount) || 0), 0)
        : 0;
      const avgGroupSize = totalGroups > 0 ? (totalParticipantsFromGroups / totalGroups).toFixed(1) : '0';

      const summaryData = [
        ['Metric', 'Value'],
        ['Total Groups', totalGroups.toString()],
        ['Total Members', uniqueMembers.toLocaleString()],
        ['Average Group Size', avgGroupSize],
      ];

      try {
        // Validate summary data
        const validSummaryData = summaryData.filter(row => 
          Array.isArray(row) && row.length === 2 && row.every(cell => typeof cell === 'string')
        );
        
        if (validSummaryData.length > 1) {
          // Render summary table
          const summaryHeaders = validSummaryData[0];
          const summaryRows = validSummaryData.slice(1);
          yPosition = renderTable(
            summaryHeaders,
            summaryRows,
            [80, 60], // Column widths: Metric, Value
            {
              startY: yPosition,
              fontSize: 11,
              headerColor: [14, 160, 133],
              rowHeight: 9, // Increased row height for better spacing
              margin: { left: 20, right: 20 }
            }
          );
          yPosition += 20; // Increased spacing after summary table
        } else {
          throw new Error('Invalid summary data');
        }
      } catch (err: any) {
        console.error('Error creating summary table:', err);
        pdf.setFontSize(10);
        pdf.setTextColor(200, 0, 0);
        pdf.text(`Error displaying executive summary: ${err.message || 'Unknown error'}`, 20, yPosition);
        yPosition += 10;
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(9);
        pdf.text(`Total Groups: ${totalGroups}`, 20, yPosition);
        yPosition += 8;
        pdf.text(`Total Members: ${uniqueMembers}`, 20, yPosition);
        yPosition += 8;
        pdf.text(`Average Group Size: ${avgGroupSize}`, 20, yPosition);
        yPosition += 15;
      }

      // Members Table
      checkPageBreak(30);
      pdf.setFontSize(18);
      pdf.setTextColor(14, 160, 133);
      const totalMembersCount = allMembersForPDF.length;
      pdf.text(`Members Details (Total: ${totalMembersCount.toLocaleString()})`, 20, yPosition);
      yPosition += 15;

      const membersTableData = allMembersForPDF.map((member: any, index: number) => {
        const memberGroups = allGroupsForPDF.filter((group: any) => {
          if (!group.participants || !Array.isArray(group.participants)) return false;
          const memberPhone = sanitizeString(member.phoneNumber || member.id);
          if (!memberPhone) return false;
          return group.participants.some((p: any) => {
            const participantPhone = sanitizeString(p.phoneNumber || p.id);
            return participantPhone && (
              participantPhone === memberPhone ||
              participantPhone === sanitizeString(member.phoneNumber) ||
              sanitizeString(p.id) === memberPhone ||
              sanitizeString(p.id) === sanitizeString(member.id)
            );
          });
        });
        
        const name = sanitizeString(member.name || member.phoneNumber || 'Unknown');
        const phone = sanitizeString(member.phoneNumber || member.id || 'N/A');
        
        return [
          String(index + 1),
          name.length > 50 ? name.substring(0, 47) + '...' : name,
          phone.length > 30 ? phone.substring(0, 27) + '...' : phone,
          String(memberGroups.length),
        ];
      });
      
      console.log('Members table data:', membersTableData.length, 'rows');

      if (membersTableData.length > 0) {
        try {
          // Validate data before creating table
          const validMembersData = membersTableData.filter(row => 
            Array.isArray(row) && row.length === 4 && row.every(cell => typeof cell === 'string')
          );
          
          if (validMembersData.length > 0) {
            // Render members table
            yPosition = renderTable(
              ['#', 'Name', 'Phone Number', 'Groups Count'],
              validMembersData,
              [15, 60, 50, 30], // Column widths
              {
                startY: yPosition,
                fontSize: 9,
                headerColor: [14, 160, 133],
                rowHeight: 8, // Increased row height for better spacing
                margin: { left: 20, right: 20 }
              }
            );
            yPosition += 20; // Increased spacing after members table
          } else {
            throw new Error('No valid members data to display');
          }
        } catch (err: any) {
          console.error('Error creating members table:', err);
          pdf.setFontSize(10);
          pdf.setTextColor(200, 0, 0);
          pdf.text(`Error displaying members table: ${err.message || 'Unknown error'}`, 20, yPosition);
          yPosition += 10;
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(9);
          pdf.text(`Total members: ${allMembersForPDF.length}`, 20, yPosition);
          yPosition += 20;
        }
      } else {
        pdf.setFontSize(10);
        pdf.text('No members data available', 20, yPosition);
        yPosition += 20;
      }

      // Groups List
      checkPageBreak(40);
      pdf.setFontSize(18);
      pdf.setTextColor(14, 160, 133);
      const totalGroupsCount = allGroupsForPDF.length;
      pdf.text(`Groups List (Total: ${totalGroupsCount.toLocaleString()})`, 20, yPosition);
      yPosition += 20; // Increased spacing after section title

      const groupsTableData = allGroupsForPDF.map((group: any, index: number) => {
        const name = sanitizeString(group.name || 'Unknown Group');
        const description = sanitizeString(group.description || '-');
        const participantsCount = Number(group.participantsCount) || 0;
        
        return [
          String(index + 1),
          name.length > 50 ? name.substring(0, 47) + '...' : name,
          String(participantsCount),
          description.length > 40 ? description.substring(0, 37) + '...' : description,
        ];
      });
      
      console.log('Groups table data:', groupsTableData.length, 'rows');

      if (groupsTableData.length > 0) {
        try {
          // Validate data before creating table
          const validGroupsData = groupsTableData.filter(row => 
            Array.isArray(row) && row.length === 4 && row.every(cell => typeof cell === 'string')
          );
          
          if (validGroupsData.length > 0) {
            // Render groups table
            yPosition = renderTable(
              ['#', 'Group Name', 'Members', 'Description'],
              validGroupsData,
              [15, 70, 25, 60], // Column widths
              {
                startY: yPosition,
                fontSize: 9,
                headerColor: [14, 160, 133],
                rowHeight: 8, // Increased row height for better spacing
                margin: { left: 20, right: 20 }
              }
            );
            yPosition += 20; // Increased spacing after groups table
          } else {
            throw new Error('No valid groups data to display');
          }
        } catch (err: any) {
          console.error('Error creating groups table:', err);
          pdf.setFontSize(10);
          pdf.setTextColor(200, 0, 0);
          pdf.text(`Error displaying groups table: ${err.message || 'Unknown error'}`, 20, yPosition);
          yPosition += 10;
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(9);
          pdf.text(`Total groups: ${allGroupsForPDF.length}`, 20, yPosition);
          yPosition += 15;
        }
      } else {
        pdf.setFontSize(10);
        pdf.text('No groups data available', 20, yPosition);
        yPosition += 15;
      }

      // Member-Group Associations (Detailed)
      if (allMembersForPDF.length > 0) {
        checkPageBreak(30);
        pdf.setFontSize(18);
        pdf.setTextColor(14, 160, 133);
        pdf.text('Member-Group Associations', 20, yPosition);
        yPosition += 15;

        allMembersForPDF.slice(0, 100).forEach((member: any) => {
          const memberGroups = allGroupsForPDF.filter((group: any) => {
            if (!group.participants || !Array.isArray(group.participants)) return false;
            const memberPhone = String(member.phoneNumber || member.id || '').trim();
            return group.participants.some((p: any) => {
              const participantPhone = String(p.phoneNumber || p.id || p.phoneNumber || '').trim();
              return participantPhone && memberPhone && (
                participantPhone === memberPhone || 
                participantPhone === String(member.phoneNumber || '').trim() ||
                String(p.id || '').trim() === memberPhone ||
                String(p.id || '').trim() === String(member.id || '').trim()
              );
            });
          });

          if (memberGroups.length > 0) {
            checkPageBreak(20);
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');
            const memberName = String(member.name || member.phoneNumber || 'Unknown');
            const memberPhone = String(member.phoneNumber || member.id || 'N/A');
            pdf.text(`${memberName} (${memberPhone})`, 20, yPosition);
            yPosition += 8;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 100, 100);
            memberGroups.slice(0, 10).forEach((group: any) => {
              checkPageBreak(8);
              const groupName = String(group.name || 'Unknown Group');
              const memberCount = String(group.participantsCount || 0);
              pdf.text(`  • ${groupName} (${memberCount} members)`, 25, yPosition);
              yPosition += 6;
            });

            if (memberGroups.length > 10) {
              pdf.text(`  ... and ${memberGroups.length - 10} more groups`, 25, yPosition);
              yPosition += 6;
            }
            yPosition += 5;
          }
        });
      }

      // Footer on each page
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${i} of ${pageCount} - ${accountName} Report`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      pdf.save(`${accountName}_Detailed_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF report. Please try again.');
    }
  };

  // Memoized filter handlers for overlap
  const handleOverlapSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleMinGroupsChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setMinGroupsFilter(e.target.value === '' ? '' : Number(e.target.value));
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, order] = e.target.value.split('-');
    setSortBy(field as 'name' | 'groups' | 'phone');
    setSortOrder(order as 'asc' | 'desc');
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  // Calculate filtered and sorted members for overlap tab - use debounced search
  const filteredOverlapMembers = useMemo(() => {
    if (!memberOverlap?.members) return [];

    let filtered = [...memberOverlap.members];

    // Apply search filter - use debounced search term
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (member: any) =>
          (member.name || 'Unknown').toLowerCase().includes(searchLower) ||
          (member.phoneNumber || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply min groups filter
    if (minGroupsFilter !== '') {
      filtered = filtered.filter(
        (member: any) => member.groups.length >= minGroupsFilter
      );
    }

    // Apply sorting
    filtered.sort((a: any, b: any) => {
      let aValue: any;
      let bValue: any;

      if (sortBy === 'groups') {
        aValue = a.groups.length;
        bValue = b.groups.length;
      } else if (sortBy === 'name') {
        aValue = (a.name || 'Unknown').toLowerCase();
        bValue = (b.name || 'Unknown').toLowerCase();
      } else {
        aValue = (a.phoneNumber || '').toLowerCase();
        bValue = (b.phoneNumber || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [memberOverlap, debouncedSearchTerm, minGroupsFilter, sortBy, sortOrder]);

  // Calculate pagination for filtered members (must be before early returns)
  const paginatedOverlapMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredOverlapMembers.slice(startIndex, endIndex);
  }, [filteredOverlapMembers, currentPage, itemsPerPage]);

  const totalOverlapPages = Math.ceil(filteredOverlapMembers.length / itemsPerPage);

  // Memoized member-to-groups mapping for reports tab (expensive operation) - uses unique groups/members
  const memberGroupsMap = useMemo(() => {
    if (!Array.isArray(uniqueReportGroups) || !Array.isArray(uniqueReportMembers)) {
      return new Map();
    }
    const map = new Map();
    uniqueReportMembers.forEach((member: any) => {
      const memberPhone = member.phoneNumber || member.id;
      if (!memberPhone) return;
      
      const groups = uniqueReportGroups.filter((group: any) => {
        if (!group.participants || !Array.isArray(group.participants)) return false;
        return group.participants.some((p: any) => 
          p.phoneNumber === memberPhone || p.id === memberPhone
        );
      });
      map.set(memberPhone, groups);
    });
    return map;
  }, [uniqueReportGroups, uniqueReportMembers]);

  // Filtered and paginated members for reports tab - use debounced search (unique members only)
  const filteredReportMembers = useMemo(() => {
    if (!Array.isArray(uniqueReportMembers)) return [];
    if (!debouncedMemberSearch) return uniqueReportMembers;
    
    const searchLower = debouncedMemberSearch.toLowerCase();
    return uniqueReportMembers.filter((member: any) => 
      (member.name || '').toLowerCase().includes(searchLower) ||
      (member.phoneNumber || '').toLowerCase().includes(searchLower)
    );
  }, [uniqueReportMembers, debouncedMemberSearch]);

  const paginatedReportMembers = useMemo(() => {
    const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
    const endIndex = startIndex + reportItemsPerPage;
    return filteredReportMembers.slice(startIndex, endIndex);
  }, [filteredReportMembers, reportCurrentPage, reportItemsPerPage]);

  const totalReportPages = useMemo(() => 
    Math.ceil(filteredReportMembers.length / reportItemsPerPage),
    [filteredReportMembers.length, reportItemsPerPage]
  );

  // Memoized filtered groups for reports sidebar (unique groups only)
  const filteredReportGroups = useMemo(() => {
    if (!Array.isArray(uniqueReportGroups)) return [];
    if (!debouncedGroupSearch) return uniqueReportGroups;
    
    const searchLower = debouncedGroupSearch.toLowerCase();
    return uniqueReportGroups.filter((group: any) =>
      group.name?.toLowerCase().includes(searchLower)
    );
  }, [uniqueReportGroups, debouncedGroupSearch]);

  // Memoized groups for selected member (unique groups only)
  const selectedMemberGroups = useMemo(() => {
    if (!selectedMember || !Array.isArray(uniqueReportGroups)) return [];
    const memberPhone = selectedMember.phoneNumber || selectedMember.id;
    if (!memberPhone) return [];
    
    return filteredReportGroups.filter((group: any) => {
      if (!group.participants || !Array.isArray(group.participants)) return false;
      return group.participants.some((p: any) => 
        p.phoneNumber === memberPhone || p.id === memberPhone
      );
    });
  }, [selectedMember, filteredReportGroups, uniqueReportGroups]);

  // Memoized chart data transformations for overview tab (must be before early returns)
  const groupsBySizeData = useMemo(() => {
    if (!data?.groupsBySize) return [];
    return data.groupsBySize.map((item: any) => ({
      name: item._id === '1000+' ? '1000+' : `0-${item._id}`,
      value: item.count,
    }));
  }, [data?.groupsBySize]);

  const accountStatusData = useMemo(() => {
    if (!data?.accountStatus) return [];
    return data.accountStatus.map((item: any) => ({
      name: item._id,
      value: item.count,
    }));
  }, [data?.accountStatus]);

  const groupsByAccountData = useMemo(() => {
    if (!data?.groupsByAccount) return [];
    return data.groupsByAccount.map((item: any) => ({
      name: item.accountName,
      groups: item.groupsCount,
      members: item.membersCount,
    }));
  }, [data?.groupsByAccount]);

  const topGroupsData = useMemo(() => {
    if (!data?.topGroups) return [];
    return data.topGroups.map((group: any) => ({
      name: group.name.length > 20 ? group.name.substring(0, 20) + '...' : group.name,
      members: group.participantsCount,
    }));
  }, [data?.topGroups]);

  // Memoized chart data for account comparison tab
  const comparisonChartData = useMemo(() => {
    if (!accountComparison || accountComparison.length === 0) {
      return { labels: [], groupsData: [], membersData: [] };
    }
    return {
      labels: accountComparison.map((acc: any) => acc.accountName),
      groupsData: accountComparison.map((acc: any) => acc.groupsCount),
      membersData: accountComparison.map((acc: any) => acc.membersCount),
    };
  }, [accountComparison]);

  if (loading && !data) {
    return <Loader fullScreen text="Loading analytics..." />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="analytics-page">
      <h1>Analytics Dashboard</h1>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'comparison' ? 'active' : ''}`}
          onClick={() => handleTabChange('comparison')}
        >
          Account Comparison
        </button>
        <button
          className={`tab ${activeTab === 'overlap' ? 'active' : ''}`}
          onClick={() => handleTabChange('overlap')}
        >
          Member Overlap
        </button>
        <button
          className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => handleTabChange('reports')}
        >
          Reports
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="analytics-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Accounts</h3>
              <div className="value">{data.overview.totalAccounts}</div>
            </div>
            <div className="stat-card">
              <h3>Connected Accounts</h3>
              <div className="value">{data.overview.connectedAccounts}</div>
            </div>
            <div className="stat-card">
              <h3>Total Groups</h3>
              <div className="value">{data.overview.totalGroups}</div>
            </div>
            <div className="stat-card">
              <h3>Unique Groups</h3>
              <div className="value">{data.overview.uniqueGroups ?? data.overview.totalGroups}</div>
            </div>
            <div className="stat-card">
              <h3>Total Members</h3>
              <div className="value">{data.overview.totalMembers.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <h3>Unique Members</h3>
              <div className="value">{(data.overview.uniqueMembers ?? data.overview.totalMembers).toLocaleString()}</div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="card chart-card">
              <h2>Groups by Account</h2>
              <div style={{ height: '400px', position: 'relative' }}>
                <MemoizedBarChart
                  data={{
                    labels: groupsByAccountData.map((item: any) => item.name),
                    datasets: [
                      {
                        label: 'Groups',
                        data: groupsByAccountData.map((item: any) => item.groups),
                        backgroundColor: '#0e4842',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="card chart-card">
              <h2>Members by Account</h2>
              <div style={{ height: '400px', position: 'relative' }}>
                <MemoizedBarChart
                  data={{
                    labels: groupsByAccountData.map((item: any) => item.name),
                    datasets: [
                      {
                        label: 'Members',
                        data: groupsByAccountData.map((item: any) => item.members),
                        backgroundColor: '#14a085',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="card chart-card">
              <h2>Top Groups by Size</h2>
              <div style={{ height: '400px', position: 'relative' }}>
                <MemoizedBarChart
                  data={{
                    labels: topGroupsData.map((item: any) => item.name),
                    datasets: [
                      {
                        label: 'Members',
                        data: topGroupsData.map((item: any) => item.members),
                        backgroundColor: '#32ff7e',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="card chart-card">
              <h2>Groups Distribution by Size</h2>
              <div style={{ height: '400px', position: 'relative' }}>
                <MemoizedDoughnutChart
                  data={{
                    labels: groupsBySizeData.map((item: any) => item.name),
                    datasets: [
                      {
                        data: groupsBySizeData.map((item: any) => item.value),
                        backgroundColor: groupsBySizeData.map((_: any, index: number) => 
                          COLORS[index % COLORS.length]
                        ),
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right' as const,
                      },
                      tooltip: {
                        callbacks: {
                          label: (context: any) => {
                            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                            const percentage = ((context.parsed / total) * 100).toFixed(0);
                            return `${context.label}: ${percentage}%`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="card chart-card">
              <h2>Account Status Distribution</h2>
              <div style={{ height: '400px', position: 'relative' }}>
                <MemoizedDoughnutChart
                  data={{
                    labels: accountStatusData.map((item: any) => item.name),
                    datasets: [
                      {
                        data: accountStatusData.map((item: any) => item.value),
                        backgroundColor: accountStatusData.map((_: any, index: number) => 
                          COLORS[index % COLORS.length]
                        ),
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right' as const,
                      },
                      tooltip: {
                        callbacks: {
                          label: (context: any) => {
                            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                            const percentage = ((context.parsed / total) * 100).toFixed(0);
                            return `${context.label}: ${percentage}%`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'comparison' && (
        <div className="analytics-content">
          {comparisonLoading ? (
            <Loader text="Loading account comparison data..." />
          ) : (
            <>
          {accountComparison.length > 0 ? (
            <>
              <div className="card">
                <h2>Groups Comparison</h2>
                <div style={{ height: '400px', position: 'relative' }}>
                  <MemoizedBarChart
                    data={{
                      labels: comparisonChartData.labels,
                      datasets: [
                        {
                          label: 'Groups',
                          data: comparisonChartData.groupsData,
                          backgroundColor: '#0e4842',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="card">
                <h2>Members Comparison</h2>
                <div style={{ height: '400px', position: 'relative' }}>
                  <MemoizedBarChart
                    data={{
                      labels: comparisonChartData.labels,
                      datasets: [
                        {
                          label: 'Members',
                          data: comparisonChartData.membersData,
                          backgroundColor: '#14a085',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="card">
              <p>No account comparison data available</p>
            </div>
          )}

          <div className="card">
            <h2>Account Details</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Status</th>
                  <th>Groups</th>
                  <th>Members</th>
                  <th>Avg Group Size</th>
                  <th>Last Sync</th>
                </tr>
              </thead>
              <tbody>
                {accountComparison.map((account: any) => (
                  <tr key={account.accountId}>
                    <td>{account.accountName}</td>
                    <td>
                      <span className={`status-badge status-${account.status}`}>
                        {account.status}
                      </span>
                    </td>
                    <td>{account.groupsCount.toLocaleString()}</td>
                    <td>{account.membersCount.toLocaleString()}</td>
                    <td>{account.avgGroupSize.toLocaleString()}</td>
                    <td>
                      {account.lastSync
                        ? new Date(account.lastSync).toLocaleString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'overlap' && (
        <div className="analytics-content">
          {overlapLoading ? (
            <Loader text="Loading member overlap data..." />
          ) : memberOverlap ? (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Unique Members</h3>
                  <div className="value">{memberOverlap.totalUniqueMembers?.toLocaleString() || 0}</div>
                </div>
                <div className="stat-card">
                  <h3>Members in Multiple Groups</h3>
                  <div className="value">{memberOverlap.overlappingMembersCount || 0}</div>
                </div>
              </div>

              <div className="card">
                <div className="table-header">
                  <h2>Members in Multiple Groups</h2>
                </div>
                <div className="filters-container">
                  <div className="filter-item">
                    <label className="filter-label">Search</label>
                    <div className="search-input-wrapper">
                      <input
                        type="text"
                        placeholder="Search by name or phone..."
                        className="search-input"
                        value={searchTerm}
                        onChange={handleOverlapSearchChange}
                      />
                    </div>
                  </div>
                  <div className="filter-item">
                    <label className="filter-label">Min Groups</label>
                    <select
                      className="filter-select"
                      value={minGroupsFilter}
                      onChange={handleMinGroupsChange}
                    >
                      <option value="">All Groups</option>
                      <option value="2">2+ Groups</option>
                      <option value="3">3+ Groups</option>
                      <option value="5">5+ Groups</option>
                      <option value="10">10+ Groups</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label className="filter-label">Sort By</label>
                    <select
                      className="filter-select"
                      value={`${sortBy}-${sortOrder}`}
                      onChange={handleSortChange}
                    >
                      <option value="groups-desc">Groups (High to Low)</option>
                      <option value="groups-asc">Groups (Low to High)</option>
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="phone-asc">Phone (A-Z)</option>
                      <option value="phone-desc">Phone (Z-A)</option>
                    </select>
                  </div>
                </div>

                {memberOverlap.members && memberOverlap.members.length > 0 ? (
                  <>
                    <div className="table-container">
                      <table className="table overlap-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Phone Number</th>
                            <th>Number of Groups</th>
                            <th>Groups</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedOverlapMembers.length > 0 ? (
                            paginatedOverlapMembers.map((member: any, index: number) => {
                              const globalIndex = (currentPage - 1) * itemsPerPage + index;
                              return (
                                <tr key={globalIndex}>
                                  <td>{globalIndex + 1}</td>
                                  <td>
                                    <strong>{member.name || 'Unknown'}</strong>
                                  </td>
                                  <td>{member.phoneNumber || 'N/A'}</td>
                                  <td>
                                    <span className="groups-badge">{member.groups.length}</span>
                                  </td>
                                  <td>
                                    <div className="groups-list">
                                      {member.groups.slice(0, 3).map((group: any, idx: number) => (
                                        <span key={idx} className="group-tag">
                                          {group.name || group.groupName || 'Unknown Group'}
                                        </span>
                                      ))}
                                      {member.groups.length > 3 && (
                                        <span className="group-tag more">
                                          +{member.groups.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="no-data">
                                No members found matching your filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="pagination-container">
                      <div className="pagination-info">
                        Showing {filteredOverlapMembers.length > 0 ? Math.min((currentPage - 1) * itemsPerPage + 1, filteredOverlapMembers.length) : 0} to{' '}
                        {Math.min(currentPage * itemsPerPage, filteredOverlapMembers.length)} of {filteredOverlapMembers.length} members
                      </div>
                      <div className="pagination-controls">
                        <select
                          className="items-per-page-select"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                          <option value="100">100 per page</option>
                        </select>
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >
                          First
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="page-info">
                          Page {currentPage} of {totalOverlapPages || 1}
                        </span>
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage((prev) => Math.min(totalOverlapPages, prev + 1))}
                          disabled={currentPage >= totalOverlapPages}
                        >
                          Next
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(totalOverlapPages)}
                          disabled={currentPage >= totalOverlapPages}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <p>No overlapping members found</p>
                )}
              </div>
            </>
          ) : (
            <Loader text="Loading member overlap data..." />
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="analytics-content">
          <div className="card" style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Account Reports</h2>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '1rem',
                  }}
                >
                  <option value="">Select Account</option>
                  {accounts.map((account: any) => (
                    <option key={account._id} value={account._id}>
                      {account.accountName} ({account.phoneNumber})
                    </option>
                  ))}
                </select>
                {selectedAccountId && (
                  <button
                    onClick={downloadPDF}
                    style={{
                      padding: '0.5rem 1.5rem',
                      backgroundColor: '#0e4842',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                    }}
                  >
                    Download PDF Report
                  </button>
                )}
              </div>
            </div>
          </div>

          {reportLoading ? (
            <Loader text="Loading report data..." />
          ) : selectedAccountId && uniqueReportGroups.length > 0 ? (
            <div id="report-content">
              {/* KPI Cards */}
              <div className="stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="stat-card">
                  <h3>Total Groups</h3>
                  <div className="value">{uniqueReportGroups.length}</div>
                </div>
                <div className="stat-card">
                  <h3>Total Members</h3>
                  <div className="value">
                    {uniqueReportMembers.length.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Main Content with Sidebar */}
              <div style={{ display: 'flex', gap: '2rem' }}>
                {/* Members Table */}
                <div className="card" style={{ flex: '2' }}>
                  <h2>Members</h2>
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      placeholder="Search members..."
                      value={memberSearch}
                      onChange={(e) => {
                        setMemberSearch(e.target.value);
                        setReportCurrentPage(1);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          setMemberSearch((e.target as HTMLInputElement).value);
                          setReportCurrentPage(1);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone Number</th>
                          <th>Groups Count</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedReportMembers.length > 0 ? (
                          paginatedReportMembers.map((member: any) => {
                            const memberPhone = member.phoneNumber || member.id;
                            const memberGroups = memberGroupsMap.get(memberPhone) || [];
                            return (
                              <tr
                                key={memberPhone}
                                onClick={() => setSelectedMember(member)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>{member.name || 'Unknown'}</td>
                                <td>{memberPhone || 'N/A'}</td>
                                <td>{memberGroups.length}</td>
                                <td>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedMember(member);
                                    }}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      backgroundColor: '#14a085',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    View Groups
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} className="no-data">
                              No members found matching your filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredReportMembers.length > 0 && (
                    <div className="pagination-container" style={{ marginTop: '1rem' }}>
                      <div className="pagination-info">
                        Showing {Math.min((reportCurrentPage - 1) * reportItemsPerPage + 1, filteredReportMembers.length)} to{' '}
                        {Math.min(reportCurrentPage * reportItemsPerPage, filteredReportMembers.length)} of {filteredReportMembers.length} members
                      </div>
                      <div className="pagination-controls">
                        <select
                          className="items-per-page-select"
                          value={reportItemsPerPage}
                          onChange={(e) => {
                            setReportItemsPerPage(Number(e.target.value));
                            setReportCurrentPage(1);
                          }}
                        >
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                          <option value="100">100 per page</option>
                        </select>
                        <button
                          className="pagination-btn"
                          onClick={() => setReportCurrentPage(1)}
                          disabled={reportCurrentPage === 1}
                        >
                          First
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => setReportCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={reportCurrentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="page-info">
                          Page {reportCurrentPage} of {totalReportPages || 1}
                        </span>
                        <button
                          className="pagination-btn"
                          onClick={() => setReportCurrentPage((prev) => Math.min(totalReportPages, prev + 1))}
                          disabled={reportCurrentPage >= totalReportPages}
                        >
                          Next
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => setReportCurrentPage(totalReportPages)}
                          disabled={reportCurrentPage >= totalReportPages}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Groups Sidebar */}
                <div className="card" style={{ flex: '1', maxWidth: '400px' }}>
                  <h2>
                    {selectedMember 
                      ? `Groups for ${selectedMember.name || 'Member'}${selectedMember.phoneNumber ? ` (${selectedMember.phoneNumber})` : ''}` 
                      : 'All Groups'}
                  </h2>
                  <div style={{ marginBottom: '1rem' }}>
                    <input
                      type="text"
                      placeholder="Search groups..."
                      value={groupSearch}
                      onChange={(e) => setGroupSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {selectedMember ? (
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        {selectedMemberGroups.map((group: any) => (
                          <li
                            key={group._id}
                            style={{
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              backgroundColor: '#f9fafb',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {group.name}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>
                              {group.participantsCount || 0} members
                            </div>
                            {selectedMember && (
                              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                                <strong>Member:</strong> {selectedMember.name || 'Unknown'} ({selectedMember.phoneNumber || 'N/A'})
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        {filteredReportGroups.map((group: any) => (
                          <li
                            key={group._id}
                            style={{
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              backgroundColor: '#f9fafb',
                              borderRadius: '4px',
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {group.name}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {group.participantsCount || 0} members
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : selectedAccountId ? (
            <div className="card">
              <p>No data available for selected account</p>
            </div>
          ) : (
            <div className="card">
              <p>Please select an account to view reports</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Analytics;

